import { useMemo } from 'react'
import { useAuth } from './useAuth'
import { useCapabilities } from './useCapabilities'
import type { CapabilitySet } from './useCapabilities'
import type { UserRole } from '@/types/auth'
import { isPlatformRole, PERMISSIONS } from '@/types/auth'

/** Auditor roles from ADR-0040: read-only access to compliance evidence. */
const AUDITOR_ROLES: UserRole[] = ['platform_auditor']

/**
 * Persona represents a set of high-level capabilities derived from the user's role.
 * Components use these capabilities to show/hide UI elements rather than
 * checking individual permissions or role names directly.
 */
export interface Persona {
  /**
   * The session's capability set, server-answered where the server has an
   * opinion (useCapabilities' SERVER_ANSWERABLE list) and role-derived
   * otherwise.
   *
   * Every other field here is computed from `role` alone, which is why a
   * CUSTOM ROLE is invisible to them: a custom role presents its base role
   * and adds capabilities on top, so a grant beyond that base cannot change
   * any of the booleans below. Predicates that need to honour such a grant
   * read this instead.
   *
   * OPTIONAL on purpose. Tests across the app build personas as partial
   * literals cast to Persona (`{ isTenant: true } as unknown as Persona`),
   * and making this required would break them at runtime rather than at the
   * type level — which is exactly how a previous change to this shape broke
   * six suites. Read it with `p.caps?.['x']`.
   */
  caps?: CapabilitySet
  /** Current user role */
  role: UserRole | null
  /** Human-readable label for the current role */
  roleLabel: string
  /** Whether this is a platform-level user */
  isPlatform: boolean
  /** Whether this is a tenant-level user */
  isTenant: boolean

  // Workflow capabilities
  canCreateWorkflow: boolean
  canEditWorkflow: boolean
  canDeleteWorkflow: boolean
  canDeployWorkflow: boolean
  canExecuteWorkflow: boolean

  // Execution capabilities
  canViewExecutions: boolean
  canCancelExecution: boolean
  canRetryExecution: boolean

  // User management capabilities
  canManageUsers: boolean
  canInviteUsers: boolean

  // Cost/budget capabilities
  canViewCost: boolean
  canManageBudget: boolean

  // Settings capabilities
  canEditSettings: boolean
  canViewSettings: boolean

  // Agent capabilities
  canCreateAgents: boolean
  canExecuteAgents: boolean

  // Audit capabilities
  canViewAudit: boolean
  /** True when the user may export signed compliance bundles (audit:export). */
  canExportAudit: boolean
  /**
   * True when the user is in a read-only auditor role (platform_auditor or
   * tenant_viewer with AUDIT_READ but no write permissions).  Use this to
   * hide — not just disable — edit/delete buttons.
   */
  isAuditor: boolean

  // Tenant management (platform-level)
  canManageTenants: boolean
  canDeleteTenant: boolean

  // AI capabilities (tier enforcement is on backend via OPA ai_access.rego)
  canUseAI: boolean

  /**
   * True for tenant_owner / tenant_admin / tenant_operator / platform_admin
   * — the tenant roles that get execute-adjacent row actions (execute,
   * trigger, pause, resume, cancel, retry, enable/disable) on at least one
   * tenant resource type in internal/authz/opa/policies/rbac.rego (schedule,
   * chain, pipeline, dagster, airflow, team_run, agent_execution,
   * mcp_tool, backfill_plan, stategraph, ...), plus platform_admin, which
   * rbac.rego grants every permission (mirrors every hasPermission()-backed
   * canX boolean above, which platform_admin also passes transparently via
   * ROLE_PERMISSIONS['platform_admin'] = Object.values(PERMISSIONS)).
   * tenant_viewer never gets execute/create/update/delete on ANY resource
   * type there — it is read/list-only everywhere. Use this to gate
   * row-level execute/trigger/pause/resume/cancel/retry/enable-disable
   * actions for domains that don't have a dedicated canX permission above
   * (e.g. schedules, runbooks, DAGs, chains, pipelines). Allowlist by
   * construction — platform_operator/platform_viewer/etc. are false; where
   * a surface's backend also grants platform_operator (varies per domain),
   * gate that surface with its own hasRole([...]) instead of this boolean.
   */
  canOperateTenantResources: boolean
  /**
   * True for tenant_owner / tenant_admin / platform_admin — the roles that
   * get destructive/authoring row actions (delete, archive, revoke,
   * promote, rotate) in rbac.rego. tenant_operator explicitly does NOT get
   * delete on workflow/chain/pipeline/team/dagster/airflow/cluster/
   * mcp_server — only owner/admin (and platform_admin, which rbac.rego
   * grants every permission) do. Use this to gate row-level delete/
   * archive/revoke/rotate/promote actions for domains without a dedicated
   * permission above. Allowlist by construction — platform_operator is
   * deliberately excluded; verify per-surface if a domain grants it.
   */
  canManageTenantResources: boolean

  // Generic check
  can: (permission: string) => boolean
}

/**
 * Exported (issue #212 fix) so shared surfaces outside this hook — e.g.
 * PendingApprovalBanner (web/src/components/shared) — can render a
 * human-readable role name from a raw UserRole slug without duplicating
 * this mapping. Keep in sync with UserRole (web/src/types/auth.ts).
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  // Platform Core
  platform_admin: 'Platform Admin',
  platform_operator: 'Platform Operator',
  platform_viewer: 'Platform Viewer',
  platform_auditor: 'Platform Auditor',
  // Platform Operations
  platform_sre: 'SRE',
  platform_infra_admin: 'Infrastructure Admin',
  platform_app_operator: 'App Operator',
  platform_security_operator: 'Security Operator',
  platform_observer: 'Observer',
  platform_debugger: 'Debugger',
  platform_on_call: 'On-Call',
  // Platform Finance
  platform_finance_admin: 'Finance Admin',
  platform_finance_analyst: 'Finance Analyst',
  platform_billing_ops: 'Billing Ops',
  platform_cost_engineer: 'Cost Engineer',
  // Platform Support
  platform_support_l1: 'Support L1',
  platform_support_l2: 'Support L2',
  platform_support_l3: 'Support L3',
  platform_support_manager: 'Support Manager',
  // Tenant Core
  tenant_owner: 'Tenant Owner',
  tenant_admin: 'Tenant Admin',
  tenant_operator: 'Operator',
  tenant_viewer: 'Viewer',
  // Tenant Finance
  tenant_billing_admin: 'Billing Admin',
  tenant_finance_viewer: 'Finance Viewer',
  tenant_cost_center_admin: 'Cost Center Admin',
  // Tenant Approval specialised roles
  approval_approver: 'Approval Approver',
  approval_viewer: 'Approval Viewer',
}

/**
 * Hook that provides persona-based capability checks for UI components.
 *
 * Use this instead of checking roles/permissions directly in components.
 * This ensures consistent capability mapping across the entire UI.
 *
 * @example
 * ```tsx
 * const { canExecuteWorkflow, canDeleteWorkflow } = usePersona()
 *
 * return (
 *   <>
 *     {canExecuteWorkflow && <Button>Execute</Button>}
 *     {canDeleteWorkflow && <Button variant="destructive">Delete</Button>}
 *   </>
 * )
 * ```
 */
export function usePersona(): Persona {
  const caps = useCapabilities()
  const { user, hasPermission } = useAuth()

  return useMemo(() => {
    const role = user?.role ?? null
    const isPlatform = role ? isPlatformRole(role) : false
    const isTenant = role ? !isPlatformRole(role) : false

    return {

      caps,

      role,
      roleLabel: role ? ROLE_LABELS[role] : 'Unknown',
      isPlatform,
      isTenant,

      // Workflow
      canCreateWorkflow: hasPermission(PERMISSIONS.WORKFLOW_CREATE),
      canEditWorkflow: hasPermission(PERMISSIONS.WORKFLOW_UPDATE),
      canDeleteWorkflow: hasPermission(PERMISSIONS.WORKFLOW_DELETE),
      canDeployWorkflow: hasPermission(PERMISSIONS.WORKFLOW_DEPLOY),
      canExecuteWorkflow: hasPermission(PERMISSIONS.WORKFLOW_EXECUTE),

      // Execution
      canViewExecutions: hasPermission(PERMISSIONS.EXECUTION_READ),
      canCancelExecution: hasPermission(PERMISSIONS.EXECUTION_CANCEL),
      canRetryExecution: hasPermission(PERMISSIONS.EXECUTION_RETRY),

      // Users
      canManageUsers: hasPermission(PERMISSIONS.USER_CREATE) || hasPermission(PERMISSIONS.USER_UPDATE),
      canInviteUsers: hasPermission(PERMISSIONS.USER_CREATE),

      // Cost
      canViewCost: hasPermission(PERMISSIONS.COST_READ),
      canManageBudget: hasPermission(PERMISSIONS.BUDGET_CREATE) || hasPermission(PERMISSIONS.BUDGET_UPDATE),

      // Settings
      canEditSettings: hasPermission(PERMISSIONS.SETTINGS_UPDATE),
      canViewSettings: hasPermission(PERMISSIONS.SETTINGS_READ) || hasPermission(PERMISSIONS.SETTINGS_UPDATE),

      // Agents
      canCreateAgents: hasPermission(PERMISSIONS.AGENT_CREATE),
      canExecuteAgents: hasPermission(PERMISSIONS.AGENT_EXECUTE),

      // Audit
      canViewAudit: hasPermission(PERMISSIONS.AUDIT_READ),
      canExportAudit: hasPermission(PERMISSIONS.AUDIT_EXPORT),
      isAuditor: role !== null && (AUDITOR_ROLES.includes(role) || (role === 'tenant_viewer' && hasPermission(PERMISSIONS.AUDIT_READ) && !hasPermission(PERMISSIONS.SETTINGS_UPDATE))),

      // Tenant management
      canManageTenants: hasPermission(PERMISSIONS.TENANT_CREATE) || hasPermission(PERMISSIONS.TENANT_UPDATE),
      canDeleteTenant: hasPermission(PERMISSIONS.TENANT_DELETE),

      // AI (tier enforcement is on backend; frontend gates only by role)
      canUseAI: hasPermission(PERMISSIONS.AI_CHAT),

      // Generic tenant-resource tiers (see doc comments on the interface) —
      // allowlist, not a permission-string lookup, since these domains have
      // no PERMISSIONS entry of their own. platform_admin is OR'd in on both
      // — every other canX boolean above goes through hasPermission(), and
      // ROLE_PERMISSIONS['platform_admin'] = Object.values(PERMISSIONS) (see
      // web/src/types/auth.ts), so platform_admin passes all of them
      // transparently. Without this, these two allowlist-based booleans
      // would silently exclude platform_admin wherever a role-string
      // comparison is used instead of hasPermission — a real, reproducible
      // loss of function on surfaces where the backend explicitly grants
      // platform_admin (e.g. stategraph restore/execute, engine_credential
      // and connection_profile CRUD). platform_operator is deliberately NOT
      // included here — its grants vary per domain (e.g. managed-clusters
      // accepts it, /admin/catalog does not), so surfaces reachable by
      // platform_operator use their own per-surface hasRole([...]) check
      // instead of this shared boolean.
      canOperateTenantResources: role === 'tenant_owner' || role === 'tenant_admin' || role === 'tenant_operator' || role === 'platform_admin',
      canManageTenantResources: role === 'tenant_owner' || role === 'tenant_admin' || role === 'platform_admin',

      // Generic
      can: hasPermission,
    }
    // `caps` MUST be in these deps. The memo is keyed on user?.role, and a
    // custom-role change does NOT change the role — so without this the
    // grantee's next session payload would carry new capabilities and the
    // persona would keep serving the old ones. The same staleness was found
    // by mutation testing in useCapabilities' own memo.
  }, [user?.role, hasPermission, caps])
}
